/**
 * Self-contained fixtures for the "Viewing Program" +New Program doc block —
 * the active read view of a created program. Decoupled from the app's
 * `programMocks` / roster derivation (the shared widget can't reach app
 * modules), this seeds the {@link ProgramViewSim} read display as pure
 * fixture state: key-stat values, the budget/timeline meter fractions +
 * labels, a `nextDistribution` (with `inSeconds` so the live countdown runs),
 * a handful of qualified-user rows, the config-summary values, and a couple
 * attached policies. No wallet / RPC / orgApiFetch / roster selection.
 *
 * "Viewing Program" = the settled read display of a created program. The one
 * piece of live motion is the `nextDistribution` countdown — everything else
 * is a static, complete frame.
 */

import type { NextDistribution } from "./NextDistributionMeter";
import type { Participant } from "./ParticipantRow";
import { POLICY_CATALOG } from "./policyFixtures";
import type { HeaderTag } from "./ProgramActiveHeader";
import type { KeyStat } from "./ProgramKeyStats";
import type { ProgramMeter } from "./ProgramProgress";
import type { Policy, ProgramConfig } from "./types";

/** The program name shown in the active-title header + across the read view. */
export const VIEWING_PROGRAM_NAME = "Philippines Holder Rewards";

/** Status-badge fixture — an Active program (green dot). */
export const VIEWING_STATUS = {
    label: "Active",
    state: "success" as const,
    labelClassName: "text-success-base"
};

/** Active-header tag chips — target · region · funding, pre-resolved to the
 *  label + tone the header renders (`septenary` target, default region, sand
 *  funding). */
export const VIEWING_TAGS: HeaderTag[] = [
    { label: "Users", tone: "septenary" },
    { label: "Philippines", tone: "default" },
    { label: "Nimbus Treasury", tone: "sand" }
];

/** Live next-distribution cycle — `inSeconds` gives the countdown a running
 *  target (≈2d 14h), `cycleDays` the meter denominator. */
export const VIEWING_NEXT_DISTRIBUTION: NextDistribution = {
    accrued: 21000,
    projected: 35000,
    cycleDays: 7,
    inSeconds: 223200
};

/** The active-program key-stats row — five pre-formatted cells. USDv Accrued /
 *  USDv Projected mirror `VIEWING_NEXT_DISTRIBUTION` (21,000 / 35,000) — the
 *  figures the NextDistributionMeter used to render as trailing labels, now
 *  surfaced as their own cells. */
export const VIEWING_KEY_STATS: KeyStat[] = [
    { label: "Total USDv Held", value: "$96,200.00" },
    { label: "USDv Accrued", value: "$21,000.00" },
    { label: "USDv Projected", value: "$35,000.00" },
    { label: "USDv Distributed", value: "$61,000.00" },
    { label: "Users", value: "1,284" }
];

/** Budget progress meter — 61k of a 250k budget distributed so far. */
export const VIEWING_BUDGET_METER: ProgramMeter = {
    title: "Program Budget",
    fraction: 61000 / 250000,
    fillLabel: "61,000 USDv distributed",
    trailLabel: "250,000 USDv budget",
    fillColor: "bg-ember"
};

/** Timeline progress meter — day 34 of a 90-day program. */
export const VIEWING_TIMELINE_METER: ProgramMeter = {
    title: "Program Timeline",
    fraction: 34 / 90,
    fillLabel: "Day 34",
    trailLabel: "90 days",
    fillColor: "bg-olive"
};

/**
 * Read-only config-summary program — a `ProgramConfig` with `status: "active"`
 * so `ProgramConfiguration` renders its `!creation` summary path. The setters
 * the View's props type requires are supplied as no-ops by the sim (read view
 * never mutates).
 */
export const VIEWING_CONFIG: ProgramConfig = {
    status: "active",
    programType: "fixed",
    budgetMode: "fixed",
    budget: 250000,
    baseApy: 8,
    period: "3m",
    periodDays: 90,
    settlementCycleLength: 7,
    settlementCycleUnit: "days"
};

/** Attached policies for the read-only Policies list — two catalog policies
 *  the program carries (Minimum Threshold + Time Held Tranche). */
export const VIEWING_POLICIES: Policy[] = [
    POLICY_CATALOG.find((p) => p.id === "minimum-threshold") as Policy,
    POLICY_CATALOG.find((p) => p.id === "time-held-tranche") as Policy
];

/** Per-policy configured values keyed by policy id — the values the read-only
 *  rows summarize. Minimum Threshold: 1,000 USDv; Time Held Tranche: its
 *  seeded tranches serialized to JSON. */
export const VIEWING_POLICY_VALUES: Record<string, number | string> = {
    "minimum-threshold": 1000,
    "time-held-tranche": JSON.stringify([
        { amount: 30, multiplier: 1.25 },
        { amount: 90, multiplier: 1.5 }
    ])
};

/** A handful of qualified-user rows for the Qualified Users table. `onNavigate`
 *  is a no-op (the read view in the doc block has no router). */
export const VIEWING_PARTICIPANTS: Participant[] = [
    {
        id: "p1",
        name: "0x8f…4a2c · Maria Santos",
        eligibility: "threshold_met",
        balance: 42500,
        earned: 1284.5,
        pending: 62.4,
        onNavigate: (): void => {}
    },
    {
        id: "p2",
        name: "0x2b…9d71 · Juan dela Cruz",
        eligibility: "threshold_met",
        balance: 18200,
        earned: 540.12,
        pending: 27.8,
        onNavigate: (): void => {}
    },
    {
        id: "p3",
        name: "0x5c…1e88 · Ana Reyes",
        eligibility: "below_threshold",
        balance: 640,
        earned: 12.4,
        pending: 0.9,
        onNavigate: (): void => {}
    },
    {
        id: "p4",
        name: "0x9a…7f30 · Carlos Mendoza",
        eligibility: "threshold_met",
        balance: 9800,
        earned: 291.66,
        pending: 14.2,
        onNavigate: (): void => {}
    }
];
